import { useRef, useState } from "react";
import { BriefcaseIcon, ChevronDownIcon } from "lucide-react";

import { ExternalGrantStatusEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { useExternalGrants } from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { List } from "@/components/organisms/List";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

/** Whom a chat is shared with, as its thread reads it. */
type ThreadShare = {
  identifier: string;
  grantIdentifier: string;
  granteeName?: string | null;
  accountIdentifier?: string | null;
  memberName?: string | null;
};

/** One way to share a chat: with a firm, or one person working through it. */
type ThreadShareRow = {
  key: string;
  grantIdentifier: string;
  accountIdentifier: string | null;
  name: string | null;
  description: string;
};

/**
 * Beside a chat's scope, where its business's books are shared with firms:
 * whom it is shared with - a firm, everyone it hands the books on to, or one
 * person there - opened to share it with another or take it back. Whoever
 * works in the books through a firm sees only the chats shared with them, a
 * business's or a person's own; the business's own people see them all.
 * Nothing is drawn where the books are shared with no firm.
 */
export function ThreadShareChip({
  thread,
}: {
  thread: {
    identifier: string;
    shares?: ThreadShare[] | null;
    entity?: {
      id: string;
      identifier: string;
      name?: string | null;
    } | null;
  } | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  // Where the keyboard was as it opened, given back as it shuts.
  const focusedRef = useRef<HTMLElement | null>(null);
  const { given, received } = useExternalGrants();
  const business = thread?.entity ?? null;
  // The firms the chat's business shares its books with, that the reader may
  // share it with: every one, for its own people; their own, for a firm's.
  const grants = [...given, ...received].filter(
    (grant) =>
      grant.entityIdentifier === business?.identifier &&
      grant.grantStatus === ExternalGrantStatusEnum.Active,
  );
  // Who works in the books through each firm: the business's accounts held
  // through its grant.
  const { data: membersData } = useBackendQuery(Queries.ENTITY_MEMBERS_QUERY, {
    variables: { id: business?.id ?? "" },
    skip: !business || grants.length === 0,
    fetchPolicy: "cache-and-network",
  });
  const members =
    membersData?.node?.__typename === "EntityObject"
      ? membersData.node.accounts
      : [];
  const { mutate: share, loading: isSharing } = useBackendMutation({
    mutation: Mutations.SHARE_THREAD,
  });
  const { mutate: unshare, loading: isUnsharing } = useBackendMutation({
    mutation: Mutations.UNSHARE_THREAD,
  });

  const shares = thread?.shares ?? [];
  if (!thread || !business || (grants.length === 0 && shares.length === 0)) {
    return null;
  }

  const rows: ThreadShareRow[] = grants.flatMap((grant) => [
    {
      key: grant.identifier,
      grantIdentifier: grant.identifier,
      accountIdentifier: null,
      name: `Everyone at ${grant.granteeName}`,
      description: `Each person ${grant.granteeName} hands these books on to can see it.`,
    },
    ...members
      .filter((member) => member.externalGrantIdentifier === grant.identifier)
      .map((member) => ({
        key: member.identifier,
        grantIdentifier: grant.identifier,
        accountIdentifier: member.identifier,
        name: member.memberDisplayName ?? member.memberEmailAddress,
        description: `Only them, of those at ${grant.granteeName}.`,
      })),
  ]);
  const shareOf = (row: ThreadShareRow) =>
    shares.find(
      (one) =>
        one.grantIdentifier === row.grantIdentifier &&
        (one.accountIdentifier ?? null) === row.accountIdentifier,
    ) ?? null;
  const said =
    shares.length === 0
      ? "Not shared"
      : shares.length === 1
        ? `Shared with ${shares[0].accountIdentifier ? shares[0].memberName : shares[0].granteeName}`
        : `Shared with ${shares.length}`;
  const says =
    shares.length === 0
      ? `Only ${business.name}'s own people can see this chat. Press to share it with a firm its books are shared with.`
      : `Those it is shared with can see this chat, beside ${business.name}'s own people. Press to change who.`;

  const toggle = () => {
    if (!isOpen) {
      focusedRef.current = document.activeElement as HTMLElement | null;
    }
    setIsOpen(!isOpen);
  };
  const chip = (
    <Button
      variant="outline"
      size="sm"
      data-testid="thread-share"
      data-shared={shares.length > 0 ? "" : undefined}
      data-keeps-focus-ring={isOpen || undefined}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      onClick={toggle}
    >
      <Icon ReactIcon={BriefcaseIcon} className="text-muted-foreground" />
      {said}
      <Icon ReactIcon={ChevronDownIcon} />
    </Button>
  );

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      // Its chip opens it.
      isClickable={false}
      placement="top-start"
      floatingStyle={{ width: 300 }}
      focusRestoreRef={focusedRef}
      anchor={
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex">{chip}</div>
          </TooltipTrigger>
          <TooltipContent side="top">{isOpen ? "" : says}</TooltipContent>
        </Tooltip>
      }
    >
      <div data-testid="thread-share-panel">
        <List<ThreadShareRow>
          data={rows}
          getRowKey={(row) => row.key}
          onItemClick={(row) => {
            if (isSharing || isUnsharing) return;
            const standing = shareOf(row);
            void (standing
              ? unshare({
                  variables: { input: { identifier: standing.identifier } },
                })
              : share({
                  variables: {
                    input: {
                      threadIdentifier: thread.identifier,
                      externalGrantIdentifier: row.grantIdentifier,
                      ...(row.accountIdentifier
                        ? { accountIdentifier: row.accountIdentifier }
                        : {}),
                    },
                  },
                }));
          }}
          autofocus
          fillHeight={false}
          size="sm"
        >
          <List.Items<ThreadShareRow>>
            {(row: ThreadShareRow) => (
              <List.Item
                value={row}
                data-testid={`thread-share-${row.key}`}
                data-shared={shareOf(row) != null ? "" : undefined}
              >
                <div
                  className={
                    row.accountIdentifier
                      ? "pl-4 pr-1 pt-0.5 pb-0.5 min-w-0"
                      : "pl-1 pr-1 pt-0.5 pb-0.5 min-w-0"
                  }
                >
                  <Typography level="body-md">
                    {row.name}
                    {shareOf(row) != null ? " ✓" : ""}
                  </Typography>
                  <Typography
                    level="body-xs"
                    className="text-muted-foreground/80 whitespace-normal"
                  >
                    {row.description}
                  </Typography>
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      </div>
    </Popper>
  );
}
